import React, { useEffect, useRef, useState } from 'react';
import filesize from 'filesize';
import { dataUrlByteSize, MagicResult } from '../model';
import { TM } from '../tokens';

const MIN_SCALE = 0.2;
const MAX_SCALE = 8;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

// Shows the generated image filling the central canvas area, with
// wheel/buttons zoom, drag-to-pan and a reset control.
export const ResultViewer: React.FC<{ result: MagicResult }> = ({ result }) => {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [dragging, setDragging] = useState(false);
  // Natural pixel size of the result image, read once it loads.
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const dragRef = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const reset = () => {
    setScale(1);
    setTx(0);
    setTy(0);
  };

  // Reset transform + measurements whenever a new image is generated.
  useEffect(() => {
    setScale(1);
    setTx(0);
    setTy(0);
    setDims(null);
  }, [result.id]);

  // Final weight: the bytes the provider recorded, else derived from the data URL.
  const bytes = result.bytes ?? (result.imageUrl ? dataUrlByteSize(result.imageUrl) : null);
  const sizeText = [dims ? `${dims.w} × ${dims.h}` : null, bytes != null ? filesize(bytes) : null]
    .filter(Boolean)
    .join(' · ');

  const zoomBy = (factor: number) => setScale((s) => clamp(s * factor, MIN_SCALE, MAX_SCALE));

  // Native non-passive wheel listener so we can preventDefault the page scroll.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setScale((s) => clamp(s * (e.deltaY < 0 ? 1.1 : 0.9), MIN_SCALE, MAX_SCALE));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!result.imageUrl) return;
    dragRef.current = { x: e.clientX, y: e.clientY, tx, ty };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    setTx(d.tx + (e.clientX - d.x));
    setTy(d.ty + (e.clientY - d.y));
  };
  const endDrag = () => {
    dragRef.current = null;
    setDragging(false);
  };

  // On-device results get their own label; cut-outs (background removed) also
  // get a transparency checkerboard behind the image so the alpha reads clearly.
  const badge =
    result.source === 'removeBg'
      ? 'Background removed'
      : result.source === 'upscale'
        ? 'Upscaled 2×'
        : `AI · seed ${result.seed} · ${result.aspect}`;
  const alpha = result.source === 'removeBg';

  return (
    <div
      ref={containerRef}
      className={alpha ? 'al-magic-result al-magic-result_alpha' : 'al-magic-result'}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
      style={{ cursor: result.imageUrl ? (dragging ? 'grabbing' : 'grab') : 'default' }}
    >
      {result.imageUrl ? (
        <img
          src={result.imageUrl}
          alt="Generated result"
          draggable={false}
          className="al-magic-result__img"
          onLoad={(e) =>
            setDims({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })
          }
          style={{
            transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
            transition: dragging ? 'none' : 'transform .12s ease',
          }}
        />
      ) : (
        <div className="al-magic-result__placeholder" style={{ background: TM.magicGradient }}>
          <div className="al-magic-result-tile__texture" />
          <span
            style={{
              position: 'relative',
              color: '#fff',
              fontFamily: 'monospace',
              fontWeight: 700,
            }}
          >
            AI · seed {result.seed}
          </span>
        </div>
      )}

      <div className="al-magic-result__badge">
        {badge}
        {result.imageUrl && sizeText && <span className="al-magic-result__size">{sizeText}</span>}
      </div>

      {result.imageUrl && (
        // Stop pointerdown here so clicking a control doesn't start a pan
        // (which would capture the pointer and swallow the button's click).
        <div className="al-magic-result__controls" onPointerDown={(e) => e.stopPropagation()}>
          <button type="button" title="Zoom out" onClick={() => zoomBy(0.8)}>
            −
          </button>
          <button type="button" title="Reset zoom" onClick={reset}>
            {Math.round(scale * 100)}%
          </button>
          <button type="button" title="Zoom in" onClick={() => zoomBy(1.25)}>
            +
          </button>
        </div>
      )}
    </div>
  );
};
